// NEW_FILE_JUSTIFIED: New hook implementing column visibility logic — the match in @types/tableColumnVisibility.ts is a doc comment reference, not an implementation.
import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '@convex/_generated/api';
import type { IColumnConfig, IColumnVisibilityReturn } from '@/@types/tableColumnVisibility';

/**
 * Manages column visibility with Convex persistence and optimistic updates.
 *
 * @param tableId - Unique identifier for the table (e.g. "insurance-policies")
 * @param columns - Full column configuration array
 * @returns Column visibility state and controls
 */
export function useColumnVisibility(
  tableId: string,
  columns: IColumnConfig[]
): IColumnVisibilityReturn {
  // Convex persistence
  const savedVisibility = useQuery(api.tableColumnVisibility.get, { tableId });
  const setVisibility = useMutation(api.tableColumnVisibility.set);

  // Compute default visible IDs from config
  const defaultVisibleIds = useMemo(
    () => columns.filter(c => c.defaultVisible || c.pinned).map(c => c.id),
    [columns]
  );

  // Local state for optimistic updates (null = use server/default value).
  // Cleared automatically when server responds with the same value — no effect needed
  // because the memo already prefers savedVisibility once optimistic is null.
  const [optimisticIds, setOptimisticIds] = useState<string[] | null>(null);

  // Resolve current visible IDs: optimistic > server > defaults
  const visibleIds = useMemo(() => {
    if (optimisticIds !== null) return optimisticIds;
    if (savedVisibility !== undefined && savedVisibility !== null) return savedVisibility;
    return defaultVisibleIds;
  }, [optimisticIds, savedVisibility, defaultVisibleIds]);

  // Build a Set for O(1) lookups
  const visibleSet = useMemo(() => new Set(visibleIds), [visibleIds]);

  // Ensure pinned columns are always in the visible set
  const pinnedIds = useMemo(() => new Set(columns.filter(c => c.pinned).map(c => c.id)), [columns]);

  const isColumnVisible = useCallback(
    (id: string) => pinnedIds.has(id) || visibleSet.has(id),
    [pinnedIds, visibleSet]
  );

  // Derived column arrays
  const visibleColumns = useMemo(
    () => columns.filter(c => isColumnVisible(c.id)),
    [columns, isColumnVisible]
  );

  const pinnedLeft = useMemo(
    () => visibleColumns.filter(c => c.pinned === 'left'),
    [visibleColumns]
  );

  const pinnedRight = useMemo(
    () => visibleColumns.filter(c => c.pinned === 'right'),
    [visibleColumns]
  );

  const scrollable = useMemo(() => visibleColumns.filter(c => !c.pinned), [visibleColumns]);

  const toggleColumn = useCallback(
    (id: string) => {
      // Don't allow toggling pinned columns
      if (pinnedIds.has(id)) return;

      const newIds = visibleSet.has(id)
        ? visibleIds.filter(vid => vid !== id)
        : [...visibleIds, id];

      // Optimistic update
      setOptimisticIds(newIds);

      // Persist to Convex
      void setVisibility({ tableId, visibleColumnIds: newIds });
    },
    [pinnedIds, visibleSet, visibleIds, tableId, setVisibility]
  );

  const resetToDefaults = useCallback(() => {
    setOptimisticIds(defaultVisibleIds);
    void setVisibility({ tableId, visibleColumnIds: defaultVisibleIds });
  }, [defaultVisibleIds, tableId, setVisibility]);

  return {
    visibleColumns,
    pinnedLeft,
    pinnedRight,
    scrollable,
    allColumns: columns,
    isColumnVisible,
    toggleColumn,
    resetToDefaults,
    isLoading: savedVisibility === undefined,
  };
}
